import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Target, Zap, Users, Shield, Heart, Linkedin, Mail, Ear, Brain, Volume2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const About = () => {
  return (
    <div data-testid="about-page" className="bg-background">
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center hero-gradient noise-overlay overflow-hidden" data-testid="about-hero">
        <div className="absolute top-1/3 right-1/3 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container-asp relative z-10 py-32">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="max-w-3xl"
          >
            <span className="text-secondary text-sm font-medium tracking-wider uppercase">
              Notre histoire
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mt-4 mb-6">
              Née sur le<br />
              <span className="text-gradient">terrain</span>
            </h1>
            <p className="text-xl text-white/60 leading-relaxed">
              Audio Sports Performance est née d'une frustration vécue au plus haut niveau. 
              Une équipe qui allie expertise terrain, maîtrise audio et innovation technologique.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="section-padding bg-background" data-testid="story-section">
        <div className="container-asp">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6">
                Le constat qui a<br />tout déclenché
              </h2>
              <Card className="card-glass p-6 mb-6">
                <p className="text-white/70 text-lg italic">
                  "J'entends mon coéquipier crier, je ne comprends pas quoi. 
                  Un centième de seconde de retard, et l'opportunité est perdue. 
                  La communication défaillante sur le terrain fait la différence 
                  entre gagner et perdre."
                </p>
              </Card>
              <p className="text-white/60 leading-relaxed mb-6">
                Hugh Chalmers, sur les terrains du Top 14, constatait ce problème 
                frustrant match après match. Jonathan Duffie, après avoir écouté 
                les enregistrements terrain réalisés avec l'UBB et l'Aviron Bayonnais, 
                s'est entendu jouer pour la première fois — une révélation. 
                La rencontre avec Eddie Ladoire, expert audio depuis 15 ans, a été le déclic.
              </p>
              <p className="text-white/60 leading-relaxed mb-6">
                <span className="text-white font-medium">
                  Si on entraîne les muscles, la technique, la tactique... 
                  pourquoi pas l'oreille ?
                </span>
              </p>
              
              {/* Hugh Chalmers quote */}
              <Card className="card-glass p-5 mb-6 border-l-4 border-primary">
                <p className="text-white/70 italic mb-2">
                  "L'audio est le seul aspect de la performance qui n'est jamais travaillé. 
                  Pourtant, un joueur ayant joué 8 ans avec le même coéquipier à sa gauche 
                  savait instinctivement où passer la balle — hauteur, vitesse, distance — 
                  uniquement à l'oreille, sans regarder."
                </p>
                <p className="text-primary text-sm font-medium">— Hugh Chalmers</p>
              </Card>

              {/* Eddie Ladoire vision */}
              <Card className="card-glass p-5 border-l-4 border-secondary">
                <p className="text-white/70 italic mb-2">
                  "Le son est une donnée brute inexploitée dans le sport. Avec l'audio 3D 
                  et l'intelligence artificielle, on peut recréer n'importe quel environnement 
                  sonore — un stade hostile, une mêlée chaotique, des consignes sous pression. 
                  L'oreille s'entraîne comme un muscle. C'est la prochaine frontière de la performance."
                </p>
                <p className="text-secondary text-sm font-medium">— Eddie Ladoire</p>
              </Card>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <Card className="card-glass p-8">
                <div className="text-center mb-8">
                  <div className="text-6xl font-bold text-gradient mb-2">2023</div>
                  <div className="text-white/50">Année de création</div>
                </div>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Target className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">Problème identifié</div>
                      <div className="text-white/50 text-sm">Sur les terrains du Top 14</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Users className="w-6 h-6 text-secondary" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">Équipe formée</div>
                      <div className="text-white/50 text-sm">Rugby × Audio × Tech</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Zap className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">Solution créée</div>
                      <div className="text-white/50 text-sm">Coach audio immersif IA</div>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="section-padding bg-gradient-to-b from-background to-black/50" data-testid="vision-section">
        <div className="container-asp">
          <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }} 
            className="max-w-4xl mx-auto"
          >
            <Card className="card-glass p-8 md:p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <Ear className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Notre vision</h2>
              <p className="text-xl text-white/60 leading-relaxed mb-8">
                "Nous avons créé un système immersif unique propulsé par l'IA, un coach audio 
                qui transforme l'oreille des joueurs en arme tactique. Avec l'audio 3D 
                et des scénarios sur mesure, chaque joueur affine ses réflexes 
                et chaque coach réinvente ses stratégies."
              </p>
              <p className="text-primary font-medium">
                C'est la nouvelle frontière de la performance pour amateurs et pros.
              </p>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-black/50" data-testid="team-section">
        <div className="container-asp">
          <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }} 
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-medium tracking-wider uppercase">
              L'équipe
            </span>
            <h2 className="text-4xl font-bold text-white mt-4">Les fondateurs</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <TeamCard 
              name="Hugh Chalmers" 
              role="Co-fondateur" 
              bio="Rugby Pro Player, Coach & Manager" 
              expertise="Vision terrain et expertise sportive"
              delay={0.1}
            />
            <TeamCard 
              name="Eddie Ladoire" 
              role="Co-fondateur" 
              bio="Expert Audio, 15 ans de production" 
              expertise="Maîtrise technique audio et spatialisation 3D"
              delay={0.2}
            />
            <TeamCard 
              name="Jonathan Duffie" 
              role="Co-fondateur" 
              bio="Ex Rugby Player" 
              expertise="Compréhension des besoins terrain"
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* Différenciation */}
      <section className="section-padding bg-background" data-testid="diff-section">
        <div className="container-asp">
          <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }} 
            className="text-center mb-16"
          >
            <span className="text-secondary text-sm font-medium tracking-wider uppercase">
              Pourquoi nous
            </span>
            <h2 className="text-4xl font-bold text-white mt-4">Ce qui nous différencie</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Target, title: 'Unique', desc: 'Aucun outil d\'entraînement audio immersif existant sur le marché' },
              { icon: Volume2, title: 'Gap identifié', desc: 'L\'audio n\'est jamais travaillé, contrairement à la vidéo omniprésente' },
              { icon: Brain, title: 'Science validée', desc: 'Un centième de seconde = la différence amateur/pro' },
              { icon: Zap, title: 'Tech mature', desc: 'Audio 3D binaural + IA disponibles et éprouvées' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                viewport={{ once: true }} 
                transition={{ delay: i * 0.1 }}
              >
                <Card className="card-glass p-6 h-full text-center">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                  <p className="text-white/50 text-sm">{item.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding bg-gradient-to-b from-background to-black/50 relative" data-testid="mission-section">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5" />
        
        <div className="container-asp relative z-10">
          <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }} 
            className="max-w-4xl mx-auto text-center"
          >
            <span className="text-secondary text-sm font-medium tracking-wider uppercase">
              Notre mission
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mt-4 mb-8">
              Faire de l'écoute un<br />
              <span className="text-gradient">standard d'entraînement</span>
            </h2>
            <p className="text-white/60 text-xl leading-relaxed mb-12">
              Notre objectif : qu'aucun club pro ou semi-pro n'imagine 
              commencer une saison sans entraînement de l'écoute.
            </p>
            <div className="flex flex-wrap justify-center gap-12">
              <div className="text-center">
                <div className="text-5xl font-bold text-gradient mb-2">5 ans</div>
                <div className="text-white/50">Pour devenir le standard</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-gradient mb-2">Mondial</div>
                <div className="text-white/50">Ambition internationale</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-gradient mb-2">15+</div>
                <div className="text-white/50">Sports collectifs visés</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-black/50" data-testid="values-section">
        <div className="container-asp">
          <motion.div 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }} 
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-medium tracking-wider uppercase">
              Nos valeurs
            </span>
            <h2 className="text-4xl font-bold text-white mt-4">Ce qui nous guide</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-6">
            <ValueCard icon={Target} title="Excellence" desc="Impact mesurable sur la performance" delay={0.1} />
            <ValueCard icon={Zap} title="Innovation" desc="Tech de pointe au service du sport" delay={0.2} />
            <ValueCard icon={Shield} title="Rigueur" desc="Approche scientifique et data-driven" delay={0.3} />
            <ValueCard icon={Heart} title="Sur-mesure" desc="Chaque club est unique" delay={0.4} />
            <ValueCard icon={Users} title="Terrain" desc="Né des besoins réels des joueurs" delay={0.5} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-background" data-testid="about-cta">
        <div className="container-asp text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-white mb-6">
              Prêt à rejoindre<br />
              <span className="text-gradient">l'aventure ?</span>
            </h2>
            <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
              Que vous soyez un club à la recherche d'un avantage compétitif 
              ou un talent qui veut faire partie de l'équipe, contactez-nous.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="btn-secondary text-base px-8 py-4 group" data-testid="about-demo-btn">
                  S'inscrire sur la liste d'attente
                  <ArrowUpRight className="ml-2 w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="btn-outline text-base px-8 py-4" data-testid="about-contact-btn">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const TeamCard = ({ name, role, bio, expertise, delay }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }} 
    whileInView={{ opacity: 1, y: 0 }} 
    viewport={{ once: true }} 
    transition={{ delay }}
  >
    <Card className="card-glass p-6 h-full hover:border-primary/30 transition-colors">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 mx-auto">
        <span className="text-xl font-bold text-white">
          {name.split(' ').map(n => n[0]).join('')}
        </span>
      </div>
      <h3 className="text-lg font-semibold text-white text-center mb-1">{name}</h3>
      <p className="text-primary text-sm text-center mb-2">{role}</p>
      <p className="text-white/60 text-sm text-center mb-2">{bio}</p>
      <p className="text-white/40 text-xs text-center italic">{expertise}</p>
      <div className="flex justify-center gap-3 mt-4">
        <a 
          href="#" 
          className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:text-primary hover:bg-white/10 transition-colors"
          aria-label={`LinkedIn de ${name}`}
        >
          <Linkedin className="w-4 h-4" />
        </a>
        <a 
          href="#" 
          className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white/40 hover:text-secondary hover:bg-white/10 transition-colors"
          aria-label={`Email de ${name}`}
        >
          <Mail className="w-4 h-4" />
        </a>
      </div>
    </Card>
  </motion.div>
);

const ValueCard = ({ icon: Icon, title, desc, delay }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }} 
    whileInView={{ opacity: 1, y: 0 }} 
    viewport={{ once: true }} 
    transition={{ delay }}
  >
    <Card className="card-glass p-6 text-center h-full hover:border-primary/30 transition-colors">
      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
        <Icon className="w-6 h-6 text-primary" />
      </div>
      <h3 className="text-white font-semibold mb-1">{title}</h3>
      <p className="text-white/50 text-sm">{desc}</p>
    </Card>
  </motion.div>
);

export default About;
